import React from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowLeft, LogOut } from "lucide-react";
import { clearParentAuth } from "@/lib/parentAuth";

/**
 * Hard-block screen shown when a parent JWT identifies an RN whose email
 * does not match the draft's `assigned_rn` field.
 *
 * Contract (matches backend `_enforce_assigned_rn`):
 *   props.assignedTo — email of the RN this visit is assigned to
 *   props.youAre     — email parsed from the bearer JWT (may be empty)
 *   props.message    — server-supplied human message ("This visit is …")
 *
 * The "Sign out" button clears the local parent JWT — useful when the
 * RN has been re-assigned mid-session and just needs a fresh deep-link
 * from the parent app to pick up a new token. Doesn't redirect; the
 * parent app owns the destination.
 */
export default function RnMismatchBlock({ assignedTo, youAre, message }) {
  const onSignOut = () => {
    clearParentAuth();
    // Strip session so a fresh deep-link can populate cleanly.
    try {
      window.sessionStorage.removeItem("wi-pa-avida-draft-id-v1");
    } catch { /* non-fatal */ }
    // Send back to landing — no router push so any in-memory state
    // resets cleanly.
    window.location.href = "/";
  };

  return (
    <main
      className="min-h-screen bg-[#F0F6FC] flex items-center justify-center px-4 py-12"
      data-testid="rn-mismatch-block"
    >
      <section className="w-full max-w-xl card-surface text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#FBECEC] flex items-center justify-center mb-4">
          <ShieldAlert className="w-7 h-7 text-[#A42A2A]" />
        </div>
        <h1
          className="font-display text-2xl font-bold text-[#0B1E3A] mb-2"
          data-testid="rn-mismatch-title"
        >
          This visit is assigned to a different RN
        </h1>
        <p className="text-[14px] text-[#3A5275] mb-6">
          {message || "This visit is assigned to a different RN."}
        </p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-[13px]">
          <div className="rounded-lg border border-[#D6E3EF] bg-white p-3 text-left">
            <dt className="text-[11px] uppercase tracking-wide text-[#3A5275] font-semibold mb-0.5">
              Assigned to
            </dt>
            <dd
              className="font-mono text-[#0B1E3A] break-all"
              data-testid="rn-mismatch-assigned-to"
            >
              {assignedTo || "—"}
            </dd>
          </div>
          <div className="rounded-lg border border-[#D6E3EF] bg-white p-3 text-left">
            <dt className="text-[11px] uppercase tracking-wide text-[#3A5275] font-semibold mb-0.5">
              You signed in as
            </dt>
            <dd
              className="font-mono text-[#0B1E3A] break-all"
              data-testid="rn-mismatch-you-are"
            >
              {youAre || "—"}
            </dd>
          </div>
        </dl>
        <p className="text-[12.5px] text-[#3A5275] italic mb-6">
          If you believe this is wrong, ask your coordinator to re-assign the
          visit, or sign out and re-open the assignment link they sent.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/"
            className="btn-ghost"
            data-testid="rn-mismatch-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to landing
          </Link>
          <button
            type="button"
            onClick={onSignOut}
            className="btn-primary"
            data-testid="rn-mismatch-signout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign out & restart
          </button>
        </div>
      </section>
    </main>
  );
}
